<script setup lang="ts">
import UserStatistics from '@/components/admin/UserStatistics.vue';
import UserActivityStatistics from '@/components/admin/UserActivityStatistics.vue';
import OrganisationStatistics from '@/components/admin/OrganisationStatistics.vue';
import VaultStatistics from '@/components/admin/VaultStatistics.vue';
import ReviewStatistics from '@/components/admin/ReviewStatistics.vue';
import ContractsStatistics from '@/components/admin/ContractsStatistics.vue';
import PlaybookStatistics from '@/components/admin/PlaybookStatistics.vue';
import DraftingStatistics from '@/components/admin/DraftingStatistics.vue';
import ChatStatistics from '@/components/admin/ChatStatistics.vue';
import SigningStatistics from '@/components/admin/SigningStatistics.vue';
import ModelCostsStatistics from '@/components/admin/ModelCostsStatistics.vue';
import { searchOrganisation } from '@/api';
import type { SelectOption } from 'naive-ui';
import { ref } from 'vue';
import dayjs from 'dayjs';
import type { StatisticsDateRange } from '@/utils/adminStatistics';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaSegmented from '@/components/design/base/GaiaSegmented.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaRemoteSelect, {
  type GaiaRemotePage,
} from '@/components/design/base/selects/GaiaRemoteSelect.vue';

const includedOrgUids = ref<string[] | undefined>(undefined);

const excludedOrgUids = ref<string[] | undefined>(undefined);

const dateRange = ref<StatisticsDateRange>(null);

type DateRangePreset = 'today' | 'last7' | 'last30' | 'last90';

const dateRangePreset = ref<DateRangePreset | null>(null);

const dateRangePresets: { label: string; value: DateRangePreset }[] = [
  { label: 'Today', value: 'today' },
  { label: 'Last 7 days', value: 'last7' },
  { label: 'Last 30 days', value: 'last30' },
  { label: 'Last 90 days', value: 'last90' },
];

const presetDaysBack: Record<DateRangePreset, number> = {
  today: 0,
  last7: 6,
  last30: 29,
  last90: 89,
};

async function fetchOrganisations(query: string): Promise<GaiaRemotePage> {
  return { options: await fetchOrganisationOptions(query) };
}

async function fetchOrganisationOptions(query: string): Promise<SelectOption[]> {
  const response = await searchOrganisation({
    body: {
      text: query,
    },
  });

  if (response.response?.status !== 200 || !response.data) {
    return [];
  }

  return response.data.results.map(org => ({
    label: org.name,
    value: org.uid,
  }));
}

function isDateRangePreset(value: string | number | boolean): value is DateRangePreset {
  return typeof value === 'string' && value in presetDaysBack;
}

function handleDateRangePreset(value: string): void {
  if (!isDateRangePreset(value)) return;
  dateRangePreset.value = value;
  const daysBack = presetDaysBack[value];
  dateRange.value = [
    dayjs().startOf('day').subtract(daysBack, 'day').valueOf(),
    dayjs().endOf('day').valueOf(),
  ];
}

// A manual edit (or clear) of the picker means the range no longer comes from
// a preset, so the segmented control lets go of its highlight.
function onDateRangePicked(): void {
  dateRangePreset.value = null;
}

// Both org pickers prime themselves on mount, so nothing to seed here.
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.wide">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.adminArea }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.statistics }}</n-breadcrumb-item>
    </template>

    <n-flex vertical :size="18" :wrap="false">
      <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
        {{ navLabels.statistics }}
      </gaia-header>
      <n-card>
        <n-flex vertical :size="20">
          <n-grid cols="1 s:1 m:2 l:3" responsive="screen" :x-gap="16" :y-gap="16">
            <n-gi>
              <n-flex vertical :size="6">
                <n-text depth="3">Included organisations</n-text>
                <gaia-remote-select
                  v-model:value="includedOrgUids"
                  multiple
                  clearable
                  prime-on-mount
                  placeholder="Include only these organisations"
                  :fetch="fetchOrganisations"
                />
              </n-flex>
            </n-gi>

            <n-gi>
              <n-flex vertical :size="6">
                <n-text depth="3">Excluded organisations</n-text>
                <gaia-remote-select
                  v-model:value="excludedOrgUids"
                  multiple
                  clearable
                  prime-on-mount
                  placeholder="Exclude these organisations"
                  :fetch="fetchOrganisations"
                />
              </n-flex>
            </n-gi>

            <n-gi>
              <n-flex vertical :size="6">
                <n-text depth="3">Date range</n-text>
                <gaia-date-picker
                  v-model:value="dateRange"
                  type="datetimerange"
                  clearable
                  style="width: 100%"
                  @update:value="onDateRangePicked"
                />
                <!-- A cleared range leaves `dateRangePreset` null; the track then
                   shows no active pill, which is exactly right. -->
                <gaia-segmented
                  :value="dateRangePreset ?? ''"
                  :options="dateRangePresets"
                  @update:value="handleDateRangePreset"
                />
              </n-flex>
            </n-gi>
          </n-grid>

          <n-tabs type="line" animated>
            <n-tab-pane name="user" tab="User">
              <UserStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="activity" tab="Activity">
              <user-activity-statistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="organisation" tab="Organisation">
              <OrganisationStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="vault" tab="Vault">
              <VaultStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="review" tab="Review">
              <ReviewStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="contracts" tab="Contracts">
              <ContractsStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="playbooks" tab="Playbooks">
              <PlaybookStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="drafting" tab="Drafting">
              <drafting-statistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="chat" tab="Chat">
              <ChatStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="signing" tab="Signing">
              <SigningStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
            <n-tab-pane name="model-costs" tab="Model Costs">
              <ModelCostsStatistics
                :included-org-uids="includedOrgUids"
                :excluded-org-uids="excludedOrgUids"
                :date-range="dateRange"
              />
            </n-tab-pane>
          </n-tabs>
        </n-flex>
      </n-card>
    </n-flex>
  </Layout>
</template>

<style scoped></style>
